/**
 * The vocabulary of «کشورها» (PM-0218, docs/country-config/CONTRACT.md §2), in
 * one place.
 *
 * Fields are the admin BFF's GraphQL names (`countries.js` typeDefs), which are
 * clinicmanagement's column names in camelCase. The closed vocabularies MIRROR
 * the service's (`app/domain/country_profile.py`): a value outside them is
 * refused there with INVALID_COUNTRY_PROFILE, so offering it here would only
 * produce an error. `PAYROLL_PROFILES` is the key accounting holds payroll
 * rules for; a row naming another key still SHOWS it. `INSURANCE_CATALOGS` is
 * only the fallback: a profile's insurance catalogue offers its own country and
 * every country with insurer rows (PM-0246, `insuranceCatalogChoices`), and
 * these two when the catalogue cannot be read.
 */

export const FALLBACK_COUNTRY = '*';
/** The URL segment of the `*` row: `/countries/fallback` (a literal `*` in a path reads as a wildcard). */
export const FALLBACK_SLUG = 'fallback';

export const countryFromParam = (param) => (param === FALLBACK_SLUG ? FALLBACK_COUNTRY : String(param || '').toUpperCase());
export const paramFromCountry = (country) => (country === FALLBACK_COUNTRY ? FALLBACK_SLUG : country);
export const countryPath = (country) => `/countries/${encodeURIComponent(paramFromCountry(country))}`;

export const CALENDARS = ['jalali', 'gregory'];
export const NATIONAL_ID_RULES = ['IR', 'GENERIC'];
export const ADDRESS_MODELS = ['iran_provinces', 'free_text'];
export const ERX_PROVIDERS = ['opnaji'];
export const COUNCIL_REGISTRIES = ['irimc'];
export const SMS_PROVIDERS = ['kavenegar'];
export const MESSAGING_CHANNELS = ['sms', 'bale', 'whatsapp'];
export const PAYMENT_RAILS = ['sepehr', 'zarinpal', 'bazaar'];
export const BILL_SERVICES = ['finnotech_inquiry', 'sepehr_bill', 'sepehr_store'];
export const PAYROLL_PROFILES = ['IR'];
export const INSURANCE_CATALOGS = ['IR', 'IQ'];
/** The languages Parsina's panels and server text speak; any ISO 639 code a row holds is still shown. */
export const LANGUAGES = ['fa', 'ar', 'en', 'fr', 'de'];
/** ISO weekdays, Monday first. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const MAX_ZOOM = 22;

/**
 * Columns nothing reads in this round (CONTRACT.md §2 «Not read in this
 * round»), with the item that will read them — null where the contract names
 * none. Shown on the screen, so nobody edits one expecting a change today.
 */
export const NOT_READ = {
  paymentRails: 'PM-0211 · PM-0090',
  weekStart: null,
  calendar: 'PM-0068',
  printCalendar: 'D12',
  printLanguage: 'D12',
};
export const isNotRead = (field) => Object.prototype.hasOwnProperty.call(NOT_READ, field);

/**
 * The DEFAULT columns a gate reads today (CONTRACT.md §5): the clinic BFF reads a
 * national number with the dial code, the forms take the national-id rule and the
 * address model, the subscription's grace rolls past the weekend counted in the
 * zone, and the currency is the country's stated one. Clearing or changing one
 * changes what every clinic of the row does, so a save asks for it to be
 * confirmed — and the admin BFF refuses the save without it (GATED_DEFAULTS
 * there; the same six).
 */
export const GATED_DEFAULT_FIELDS = [
  'nationalDialCode', 'nationalIdRule', 'addressModel', 'weekendDays', 'defaultTimezone', 'defaultCurrency',
];

/**
 * Every column, grouped as the contract groups them. `kind` decides what NULL
 * means: a DEFAULT follows `*` (and in `*`, the services' own default today); a
 * CAPABILITY that is NULL or empty is NONE — a country with a row of its own
 * never inherits one from `*`. `type` picks the control.
 */
export const COUNTRY_GROUPS = [
  {
    key: 'locale',
    fields: [
      { field: 'defaultLanguage', kind: 'default', type: 'language' },
      { field: 'calendar', kind: 'default', type: 'enum', options: CALENDARS },
      { field: 'weekStart', kind: 'default', type: 'weekday' },
      { field: 'weekendDays', kind: 'default', type: 'weekdays' },
      { field: 'defaultCurrency', kind: 'default', type: 'currency' },
      { field: 'defaultTimezone', kind: 'default', type: 'timezone' },
    ],
  },
  {
    key: 'identity',
    fields: [
      { field: 'nationalIdRule', kind: 'default', type: 'enum', options: NATIONAL_ID_RULES },
      { field: 'nationalDialCode', kind: 'default', type: 'dialCode' },
      { field: 'addressModel', kind: 'default', type: 'enum', options: ADDRESS_MODELS },
      { field: 'mapView', kind: 'default', type: 'mapView' },
    ],
  },
  {
    key: 'capabilities',
    fields: [
      { field: 'erxProvider', column: 'erx_provider', kind: 'capability', type: 'enum', options: ERX_PROVIDERS },
      { field: 'councilRegistry', column: 'council_registry', kind: 'capability', type: 'enum', options: COUNCIL_REGISTRIES },
      { field: 'smsProvider', column: 'sms_provider', kind: 'capability', type: 'enum', options: SMS_PROVIDERS },
      { field: 'messagingChannels', column: 'messaging_channels', kind: 'capability', type: 'list', options: MESSAGING_CHANNELS },
      { field: 'paymentRails', column: 'payment_rails', kind: 'capability', type: 'list', options: PAYMENT_RAILS },
      { field: 'billServices', column: 'bill_services', kind: 'capability', type: 'list', options: BILL_SERVICES },
      { field: 'payrollProfile', column: 'payroll_profile', kind: 'capability', type: 'key', options: PAYROLL_PROFILES },
      { field: 'insuranceCatalog', column: 'insurance_catalog', kind: 'capability', type: 'key', options: INSURANCE_CATALOGS },
    ],
  },
  {
    key: 'print',
    fields: [
      { field: 'printCalendar', kind: 'default', type: 'enum', options: CALENDARS },
      { field: 'printLanguage', kind: 'default', type: 'language' },
    ],
  },
];

export const ALL_FIELDS = COUNTRY_GROUPS.flatMap((g) => g.fields);
export const CAPABILITY_FIELDS = ALL_FIELDS.filter((f) => f.kind === 'capability');
export const fieldSpec = (field) => ALL_FIELDS.find((f) => f.field === field) || null;

/** `defaultLanguage` → `default_language` — the service's column name (history, removals). */
export const columnOf = (field) => field.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
/** `default_language` → `defaultLanguage`. */
export const fieldOfColumn = (column) => String(column).replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/** The three switches a clinic may turn off, and the capability each takes away. */
export const CLINIC_SWITCHES = [
  { field: 'erxEnabled', takes: ['erxProvider'] },
  { field: 'smsEnabled', takes: ['smsProvider', 'messagingChannels'] },
  { field: 'insuranceEnabled', takes: ['insuranceCatalog'] },
];

// ── The insurer catalogue editor (PM-0246) ──────────────────────────────────
// MIRRORS accounting's app/domain/insurer_catalogue.py (insureradmin
// .InsurerAdminService): a value outside these is refused there, so the
// editor never offers it. The service checks every rule again.

/** InsurerRecord.id: a slug that never changes — contracts, policies and claims point at it. */
export const INSURER_ID = /^[a-z][a-z0-9_-]{1,39}$/;
export const INSURER_ID_MAX = 40;
/** The languages an insurer is named in; Persian or English is required. */
export const INSURER_NAME_LOCALES = ['fa', 'en', 'ar', 'fr', 'de'];
export const INSURER_REQUIRED_NAMES = ['fa', 'en'];
export const INSURER_NAME_MAX = 120;
export const INSURER_ALIAS_MAX = 120;
export const INSURER_ALIASES_MAX = 50;
export const INSURER_KINDS = ['basic', 'supplementary'];
/** A registry key and a verifier exist in Iran only (op-naji's keys; تأمین's policy check). */
export const INSURER_REGISTRY_CODES = { IR: ['tamin', 'salamat', 'esakhad'] };
export const INSURER_VERIFY_PROVIDERS = { IR: ['tamin'] };
